import {
  UZUM_FONT,
  UzumMode,
  alpha,
  uzumColors as c,
  uzumSemantic,
  uzumWarning,
} from './uzumTokens';

/**
 * Backstage UI (BUI) custom properties for the Uzum themes, light and dark.
 *
 * Scoped to `body[data-theme-name='<id>']` because @backstage/ui declares its
 * defaults under `[data-theme-mode='…']`,
 * which also matches <body>, so a `:root` rule would be shadowed on the element
 * itself. The `body` prefix wins the tie on specificity.
 *
 * Both the current token families (accent, positive, negative, warning,
 * announcement) and the deprecated ones still read by older components (solid,
 * danger, success, info) are set — anything left out falls through to BUI's
 * blue defaults.
 */
function uzumBui(themeId: string, mode: UzumMode): string {
  const s = uzumSemantic[mode];
  const dark = mode === 'dark';
  const w = uzumWarning;

  const vars: Record<string, string> = {
    'font-regular': UZUM_FONT,
    'font-weight-regular': '400',
    'font-weight-bold': '700',
    black: c.black950,
    white: c.black0,
    ring: dark ? c.brand300 : c.brand400,

    'bg-app': s.background.main,
    'bg-neutral-1': s.background.sheet,
    'bg-neutral-2': s.container.grey,
    'bg-neutral-3': dark ? alpha(c.black0, 0.2) : c.blueBlack100,
    'bg-neutral-4': dark ? alpha(c.black0, 0.28) : c.blueBlack200,

    'fg-primary': s.text.primary,
    'fg-secondary': s.text.secondary,
    'fg-disabled': s.text.disabled,
    'fg-positive': s.text.success,
    'fg-negative': s.text.error,
    'fg-warning': dark ? w.main : w.dark,
    'fg-announcement': s.text.link,

    'border-1': s.border.subtle,
    'border-2': s.border.strong,

    'accent-bg': s.action.primary,
    'accent-bg-hover': s.action.primaryPressed,
    'accent-bg-disabled': s.action.primaryDisabled,
    'accent-fg': s.action.onPrimary,
    'accent-fg-disabled': s.action.onPrimaryDisabled,

    'announcement-bg': s.action.primary,
    'announcement-bg-hover': s.action.primaryPressed,
    'announcement-bg-disabled': s.action.primaryDisabled,
    'announcement-bg-subdued': s.container.accent,
    'announcement-bg-subdued-hover': dark
      ? alpha(c.brand400, 0.56)
      : c.brand300,
    'announcement-bg-subdued-disabled': s.action.secondary,
    'announcement-border': dark ? c.brand600 : c.brand300,
    'announcement-fg': s.action.onPrimary,
    'announcement-fg-disabled': s.action.onPrimaryDisabled,
    'announcement-fg-subdued': s.text.accent,
    'announcement-fg-subdued-disabled': s.text.disabled,

    'positive-bg': dark ? c.green400 : c.green600,
    'positive-bg-hover': dark ? c.green600 : c.green400,
    'positive-bg-disabled': alpha(c.green600, 0.4),
    'positive-bg-subdued': s.container.positive,
    'positive-bg-subdued-hover': dark
      ? alpha(c.brandAdd400, 0.25)
      : c.brandAdd100,
    'positive-bg-subdued-disabled': s.container.positive,
    'positive-border': dark ? alpha(c.green400, 0.5) : c.green400,
    'positive-fg': dark ? c.black950 : c.black0,
    'positive-fg-disabled': s.text.disabled,
    'positive-fg-subdued': s.text.success,
    'positive-fg-subdued-disabled': s.text.disabled,

    'negative-bg': dark ? c.red400 : c.red600,
    'negative-bg-hover': dark ? c.red600 : c.red400,
    'negative-bg-disabled': alpha(c.red600, 0.4),
    'negative-bg-subdued': s.container.negative,
    'negative-bg-subdued-hover': dark ? alpha(c.red400, 0.25) : c.red100,
    'negative-bg-subdued-disabled': s.container.negative,
    'negative-border': dark ? alpha(c.red400, 0.5) : c.red400,
    'negative-fg': c.black0,
    'negative-fg-disabled': s.text.disabled,
    'negative-fg-subdued': s.text.error,
    'negative-fg-subdued-disabled': s.text.disabled,

    'warning-bg': w.main,
    'warning-bg-hover': w.light,
    'warning-bg-disabled': alpha(w.main, 0.4),
    'warning-bg-subdued': dark ? alpha(w.main, 0.15) : '#FFF6E0',
    'warning-bg-subdued-hover': dark ? alpha(w.main, 0.25) : '#FFEBB8',
    'warning-bg-subdued-disabled': dark ? alpha(w.main, 0.15) : '#FFF6E0',
    'warning-border': dark ? alpha(w.main, 0.5) : w.main,
    'warning-fg': c.black950,
    'warning-fg-disabled': s.text.disabled,
    'warning-fg-subdued': dark ? w.main : w.dark,
    'warning-fg-subdued-disabled': s.text.disabled,

    // Deprecated tokens, still read by some components.
    'bg-solid': s.action.primary,
    'bg-solid-hover': s.action.primaryPressed,
    'bg-solid-pressed': s.action.primaryPressed,
    'bg-solid-disabled': s.action.primaryDisabled,
    'fg-solid': s.action.onPrimary,
    'fg-solid-disabled': s.action.onPrimaryDisabled,
    'bg-danger': s.container.negative,
    'bg-warning': dark ? alpha(w.main, 0.15) : '#FFF6E0',
    'bg-success': s.container.positive,
    'bg-info': s.container.accent,
    'fg-danger': s.text.error,
    'fg-success': s.text.success,
    'fg-info': s.text.link,
    'fg-danger-on-bg': s.text.error,
    'fg-warning-on-bg': dark ? w.main : w.dark,
    'fg-success-on-bg': s.text.success,
    'fg-info-on-bg': s.text.accent,
    'border-danger': c.red400,
    'border-warning': w.main,
    'border-success': c.green400,
    'border-info': dark ? c.brand600 : c.brand300,
  };

  // The nav (modules/nav/Nav.css) is ours, not a BUI component. It stays dark
  // in both modes, as the sheets' "Navigation Transp Inverse" does; in dark it
  // lifts to Black 900 to stand off the Black 950 canvas.
  const nav: Record<string, string> = {
    bg: dark ? c.black900 : c.black950,
    fg: alpha(c.black0, 0.6),
    'fg-active': c.black0,
    hover: alpha(c.black0, 0.12),
    divider: alpha(c.black0, 0.12),
    indicator: c.brand400,
    mark: c.brand700,
  };

  const body = [
    ...Object.entries(vars).map(
      ([name, value]) => `  --bui-${name}: ${value};`,
    ),
    ...Object.entries(nav).map(
      ([name, value]) => `  --atlas-nav-${name}: ${value};`,
    ),
  ].join('\n');
  // The primary action in a page header (Create, Register…) is the sheets'
  // full-size button: 44 px, radius 12 — BUI's `small` is 32 px, radius 4.
  // Unlayered, so it beats BUI's `@layer components` rules regardless of
  // specificity; scoped to the header so other BUI buttons keep their size.
  const headerButton = `
body[data-theme-name='${themeId}'] .bui-HeaderControls :is(.bui-Button, .bui-ButtonLink)[data-variant='primary'] {
  height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
}
`;
  return `\nbody[data-theme-name='${themeId}'] {\n${body}\n}\n${headerButton}`;
}

export const uzumBuiCss = uzumBui('uzum', 'light');
export const uzumDarkBuiCss = uzumBui('uzum-dark', 'dark');
